import React, { useState, useEffect } from 'react';
import { Terminal, Power, Code2, FileJson, Activity, AlertCircle, CheckCircle, Settings } from 'lucide-react';

interface Hook {
  name: string;
  event: string;
  matcher: string;
  command: string;
  enabled: boolean;
  lastFired?: string;
  fireCount: number;
}

const AVAILABLE_HOOKS = [
  { event: 'UserPromptSubmit', desc: 'Fires when user submits a prompt' },
  { event: 'PreToolUse', desc: 'Before any tool is used' },
  { event: 'PostToolUse', desc: 'After any tool is used' },
  { event: 'SessionStart', desc: 'When Claude session starts' },
  { event: 'SessionEnd', desc: 'When Claude session ends' },
  { event: 'Notification', desc: 'For system notifications' },
  { event: 'PreCompact', desc: 'Before context compaction' },
];

const DEFAULT_COMMANDS = {
  UserPromptSubmit: "echo '[HOOK] User prompt: ' >> /tmp/claude-hooks.log && cat | jq -r '.prompt' >> /tmp/claude-hooks.log",
  PreToolUse: "echo '[HOOK] Tool use: ' >> /tmp/claude-hooks.log && cat | jq -r '.tool' >> /tmp/claude-hooks.log",
  PostToolUse: "echo '[HOOK] Tool result' >> /tmp/claude-hooks.log",
  SessionStart: "echo '[HOOK] Session started at ' $(date) >> /tmp/claude-hooks.log",
  SessionEnd: "echo '[HOOK] Session ended at ' $(date) >> /tmp/claude-hooks.log",
  Notification: "echo '[HOOK] Notification' >> /tmp/claude-hooks.log",
  PreCompact: "echo '[HOOK] Context compacting' >> /tmp/claude-hooks.log",
};

export default function HookMachine() {
  const [hooks, setHooks] = useState<Hook[]>([]);
  const [logs, setLogs] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'hooks' | 'logs' | 'config'>('hooks');
  const [configJson, setConfigJson] = useState<string>('');
  const [lastUpdate, setLastUpdate] = useState<number>(Date.now());

  useEffect(() => {
    loadHooks();
    loadLogs();
    const interval = setInterval(() => {
      loadLogs();
      checkHookActivity();
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const loadHooks = async () => {
    try {
      const response = await fetch('/api/hooks/config');
      const config = await response.json();

      const hookList: Hook[] = AVAILABLE_HOOKS.map(h => ({
        name: h.event,
        event: h.event,
        matcher: '*',
        command: DEFAULT_COMMANDS[h.event as keyof typeof DEFAULT_COMMANDS] || "echo '[HOOK]' >> /tmp/claude-hooks.log",
        enabled: false,
        fireCount: 0
      }));

      if (config.hooks) {
        Object.entries(config.hooks).forEach(([event, hookConfig]: [string, any]) => {
          const hook = hookList.find(h => h.event === event);
          if (hook && Array.isArray(hookConfig) && hookConfig[0]) {
            hook.enabled = true;
            hook.matcher = hookConfig[0].matcher || '*';
            if (hookConfig[0].hooks?.[0]?.command) {
              hook.command = hookConfig[0].hooks[0].command;
            }
          }
        });
      }

      setHooks(hookList);
      setConfigJson(JSON.stringify(config, null, 2));
    } catch (error) {
      console.error('Failed to load hooks:', error);
      setHooks(AVAILABLE_HOOKS.map(h => ({
        name: h.event,
        event: h.event,
        matcher: '*',
        command: DEFAULT_COMMANDS[h.event as keyof typeof DEFAULT_COMMANDS] || '',
        enabled: false,
        fireCount: 0
      })));
    }
  };

  const loadLogs = async () => {
    try {
      const response = await fetch('/api/hooks/logs');
      const data = await response.json();
      if (data.logs) {
        setLogs(data.logs);
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
    }
  };

  const checkHookActivity = () => {
    const now = Date.now();
    setHooks(prev => prev.map(hook => {
      const recentLogs = logs.filter(log =>
        log.includes(`[HOOK]`) &&
        log.toLowerCase().includes(hook.event.toLowerCase())
      );

      if (recentLogs.length > hook.fireCount) {
        return {
          ...hook,
          fireCount: recentLogs.length,
          lastFired: new Date().toISOString()
        };
      }
      return hook;
    }));
  };

  const toggleHook = async (hookName: string) => {
    const hook = hooks.find(h => h.name === hookName);
    if (!hook) return;

    const newHooks = hooks.map(h =>
      h.name === hookName ? { ...h, enabled: !h.enabled } : h
    );
    setHooks(newHooks);

    const config = {
      hooks: {}
    };

    newHooks.filter(h => h.enabled).forEach(h => {
      config.hooks[h.event] = [{
        matcher: h.matcher,
        hooks: [{
          type: 'command',
          command: h.command
        }]
      }];
    });

    try {
      await fetch('/api/hooks/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      setConfigJson(JSON.stringify(config, null, 2));
      setLastUpdate(Date.now());
    } catch (error) {
      console.error('Failed to save hook config:', error);
    }
  };

  const updateHookCommand = (hookName: string, command: string) => {
    setHooks(prev => prev.map(h =>
      h.name === hookName ? { ...h, command } : h
    ));
  };

  const saveAllHooks = async () => {
    const config = {
      hooks: {}
    };

    hooks.filter(h => h.enabled).forEach(h => {
      config.hooks[h.event] = [{
        matcher: h.matcher,
        hooks: [{
          type: 'command',
          command: h.command
        }]
      }];
    });

    try {
      await fetch('/api/hooks/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      setConfigJson(JSON.stringify(config, null, 2));
      setLastUpdate(Date.now());
      alert('Hooks configuration saved!');
    } catch (error) {
      console.error('Failed to save hooks:', error);
      alert('Failed to save hooks configuration');
    }
  };

  const clearLogs = async () => {
    try {
      await fetch('/api/hooks/logs', { method: 'DELETE' });
      setLogs([]);
    } catch (error) {
      console.error('Failed to clear logs:', error);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-gray-900 rounded-lg shadow-2xl">
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Terminal className="w-8 h-8 text-green-400" />
              <h1 className="text-2xl font-bold text-white">Hook Machine</h1>
              <span className="px-2 py-1 bg-green-900 text-green-400 text-xs rounded-full">
                Claude Code Integration
              </span>
            </div>
            <div className="flex items-center space-x-2 text-gray-400 text-sm">
              <Activity className="w-4 h-4" />
              <span>Last update: {new Date(lastUpdate).toLocaleTimeString()}</span>
            </div>
          </div>
        </div>

        <div className="flex border-b border-gray-800">
          {(['hooks', 'logs', 'config'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === tab
                  ? 'text-green-400 border-b-2 border-green-400 bg-gray-800'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {tab === 'hooks' && <Code2 className="inline w-4 h-4 mr-2" />}
              {tab === 'logs' && <Terminal className="inline w-4 h-4 mr-2" />}
              {tab === 'config' && <FileJson className="inline w-4 h-4 mr-2" />}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'hooks' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-gray-400">
                  Configure Claude Code hooks to enhance your development workflow
                </p>
                <button
                  onClick={saveAllHooks}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center space-x-2"
                >
                  <Settings className="w-4 h-4" />
                  <span>Save Configuration</span>
                </button>
              </div>

              <div className="space-y-3">
                {hooks.map(hook => (
                  <div key={hook.name} className="bg-gray-800 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => toggleHook(hook.name)}
                            className={`p-2 rounded-lg transition-colors ${
                              hook.enabled
                                ? 'bg-green-900 text-green-400 hover:bg-green-800'
                                : 'bg-gray-700 text-gray-500 hover:bg-gray-600'
                            }`}
                          >
                            <Power className="w-5 h-5" />
                          </button>
                          <div>
                            <h3 className="font-semibold text-white">{hook.event}</h3>
                            <p className="text-sm text-gray-400">
                              {AVAILABLE_HOOKS.find(h => h.event === hook.event)?.desc}
                            </p>
                          </div>
                        </div>
                        {hook.lastFired && (
                          <div className="mt-2 flex items-center space-x-2 text-xs text-green-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Fired {hook.fireCount} times</span>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          hook.enabled
                            ? 'bg-green-900 text-green-400'
                            : 'bg-gray-700 text-gray-500'
                        }`}>
                          {hook.enabled ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>

                    {hook.enabled && (
                      <div className="mt-3">
                        <label className="block text-xs text-gray-500 mb-1">Command:</label>
                        <textarea
                          value={hook.command}
                          onChange={(e) => updateHookCommand(hook.name, e.target.value)}
                          className="w-full px-3 py-2 bg-gray-900 text-gray-300 text-sm font-mono rounded border border-gray-700 focus:border-green-500 focus:outline-none"
                          rows={2}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-gray-400">Hook execution logs from /tmp/claude-hooks.log</p>
                <button
                  onClick={clearLogs}
                  className="px-3 py-1 bg-red-900 hover:bg-red-800 text-red-400 rounded text-sm"
                >
                  Clear Logs
                </button>
              </div>
              <div className="bg-black rounded-lg p-4 max-h-96 overflow-y-auto">
                {logs.length > 0 ? (
                  <pre className="text-green-400 font-mono text-xs whitespace-pre-wrap">
                    {logs.join('\n')}
                  </pre>
                ) : (
                  <p className="text-gray-500 text-center py-8">No logs yet. Enable hooks to see activity.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'config' && (
            <div>
              <div className="mb-4">
                <p className="text-gray-400 mb-2">Current .claude/settings.json configuration:</p>
                <div className="flex items-start space-x-2 p-3 bg-blue-900 bg-opacity-20 rounded-lg border border-blue-800">
                  <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                  <p className="text-sm text-blue-400">
                    Changes require Claude Code restart to take full effect.
                    Use the Hooks tab for live configuration.
                  </p>
                </div>
              </div>
              <div className="bg-black rounded-lg p-4">
                <pre className="text-gray-300 font-mono text-xs whitespace-pre-wrap">
                  {configJson || '{}'}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}